/**
 * Setup mode (BLU-1376, handover 4.5): until a new org finishes setup, every
 * user except a platform admin sees no sidebar and can reach only the wizard
 * and the pages it opens.
 *
 * This is a UX fence only. Data stays fenced by RLS and the procedures, as
 * today. Every rule here fails open: no org, or a field missing from an older
 * backend, reads as NOT in setup mode, so a live org is never locked (G1).
 *
 * Pure fulfilment 3PL providers are never in setup (the G1 audit carried from
 * PR 1): their Shopify step can never complete without a store of their own.
 * Platform power is the platform admins list only, never a role (G2).
 *
 * Imported by file path (never through the feature barrel) so the auth guard
 * does not pull the step components into its chunk.
 */

/** The org fields the setup rule reads; all optional so an older backend fails open. */
export interface SetupModeOrg {
  setup_required?: boolean;
  setup_completed_at?: string | null;
  is_3pl_provider?: boolean;
}

/**
 * The ONE answer to "does this org still need setup": created through Create
 * store (`setup_required`), not latched, and not a 3PL provider.
 */
export function orgNeedsSetup(org: SetupModeOrg | null | undefined): boolean {
  return org?.setup_required === true && !org.setup_completed_at && !org.is_3pl_provider;
}

/** A user is in setup mode when the org needs setup and they are not a platform admin. */
export function isInSetupMode(org: SetupModeOrg | null | undefined, isPlatformAdmin: boolean): boolean {
  return !isPlatformAdmin && orgNeedsSetup(org);
}

/**
 * The sidebar "Get set up" entry gate (Q3): a platform admin on an org that
 * still needs setup. Missing fields read false (the entry is a convenience
 * link, so it fails closed). This gates the entry and its status query; it is
 * not a second latch derivation (that lives in `selectSetupProgress`).
 */
export function shouldShowSetupEntry(org: SetupModeOrg | null | undefined, isPlatformAdmin: boolean): boolean {
  return isPlatformAdmin && orgNeedsSetup(org);
}

/** Paths allowed in setup mode, with everything below them. */
const SETUP_MODE_PREFIXES = ['/onboarding', '/product-sync', '/products'] as const;
/** Paths allowed in setup mode exactly (nothing below them). */
const SETUP_MODE_EXACT = ['/inventory'] as const;

function stripTrailingSlash(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

function matchesPrefix(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`);
}

/**
 * Segment-aware allowlist (never `lib/roles.ts` `isRouteAllowedForRole`, which
 * is exact-match): `/products/abc` passes, `/productsx` and `/product-dev` do
 * not; `/inventory` passes, `/inventory/positions` does not.
 */
export function isSetupModeAllowedPath(pathname: string): boolean {
  const path = stripTrailingSlash(pathname);
  return (
    SETUP_MODE_PREFIXES.some((prefix) => matchesPrefix(path, prefix)) ||
    (SETUP_MODE_EXACT as readonly string[]).includes(path)
  );
}

/** `/onboarding` and below: where the Back to setup bar is not shown. */
export function isOnboardingPath(pathname: string): boolean {
  return matchesPrefix(stripTrailingSlash(pathname), '/onboarding');
}

/** TopHeader is `h-14`. */
export const TOP_HEADER_HEIGHT = '3.5rem';
/** The Back to setup bar's height, folded into `--top-header-height` while it shows. */
export const SETUP_BAR_HEIGHT = '2.75rem';
